import { useState, useRef, useCallback, useEffect } from "react";
import MDEditor from "@uiw/react-markdown-editor";
import MarkdownPreview from "@uiw/react-markdown-preview";
import "@uiw/react-markdown-preview/markdown.css";
import "@uiw/react-markdown-editor/markdown-editor.css";
import { useTranslation } from "../../i18n";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { apiFetch } from "../../common/api";

const HOST = import.meta.env.VITE_API_HOST;

const TITLE_KEY = "push_title_template";
const BODY_KEY = "push_body_template";

/**
 * Editor for a campaign's push title and body, with placeholder help and a
 * server-rendered preview of the finished notification.
 *
 * Both templates live in one component because a push notification is one
 * artefact: splitting them across two editors meant the title was a plain
 * input with no placeholder help and no preview at all, and the preview beside
 * the body showed the trigger's *hardcoded default* title rather than the
 * operator's, because the preview request only ever sent `bodyTemplate`.
 *
 * Both templates are sent on every preview request so the backend can render
 * the real pair — it substitutes placeholders with the campaign's own config
 * and trigger, which a client-side substitution could not do faithfully.
 *
 * Props:
 *  - config: object — the campaign config holding both templates
 *  - onChange: (name: string, value: string) => void
 *  - defaults: {title?: string, body?: string} — the trigger schema's
 *    declared defaults, shown when the config has no value yet so the fields
 *    start on the same text the preview renders
 *  - triggerType?: string — trigger key, required for the server preview
 *  - placeholders?: Array<{key, description, example}>
 *  - campaignId?: number — render with this campaign's stored config
 *  - disabled?: boolean
 */
export default function PushTemplateEditor({
  config,
  onChange,
  defaults = {},
  titleLabel,
  bodyLabel,
  disabled = false,
  triggerType,
  placeholders = [],
  campaignId = null,
}) {
  const { t } = useTranslation();
  const [showHelp, setShowHelp] = useState(false);
  const [preview, setPreview] = useState({ title: "", body: "" });
  const debounceRef = useRef(null);

  // Fall back to the schema default rather than an empty box, so the field
  // shows the same text the preview renders. `?? ""` (not `||`) so a
  // deliberately cleared field stays empty instead of snapping back.
  const title = config?.[TITLE_KEY] ?? defaults.title ?? "";
  const body = config?.[BODY_KEY] ?? defaults.body ?? "";

  const fetchPreview = useCallback((nextTitle, nextBody, type, campaign) => {
    if (!type) {
      // No trigger selected yet — echo the raw text rather than showing a
      // stale preview from a previously selected trigger.
      setPreview({ title: nextTitle || "", body: nextBody || "" });
      return;
    }
    apiFetch(`${HOST}/api/v1/campaigns/render-preview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        triggerType: type,
        titleTemplate: nextTitle || "",
        bodyTemplate: nextBody || "",
        campaignId: campaign ?? null,
      }),
    })
      .then((r) => {
        if (!r.ok) throw new Error("preview failed");
        return r.json();
      })
      .then((data) =>
        setPreview({ title: data.title || "", body: data.body || "" }),
      )
      .catch(() => {
        setPreview({ title: nextTitle || "", body: nextBody || "" });
      });
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  /** Debounced preview — fires 500 ms after the last keystroke. */
  const debouncedPreview = useCallback(
    (nextTitle, nextBody, type, campaign) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(
        () => fetchPreview(nextTitle, nextBody, type, campaign),
        500,
      );
    },
    [fetchPreview],
  );

  /** Editing either template refreshes the preview of the whole notification. */
  const handleTitleChange = useCallback(
    (value) => {
      onChange(TITLE_KEY, value);
      debouncedPreview(value, body, triggerType, campaignId);
    },
    [onChange, debouncedPreview, body, triggerType, campaignId],
  );

  const handleBodyChange = useCallback(
    (value) => {
      onChange(BODY_KEY, value);
      debouncedPreview(title, value, triggerType, campaignId);
    },
    [onChange, debouncedPreview, title, triggerType, campaignId],
  );

  /** Final preview fetch on blur — no debounce, immediate. */
  const handleBlur = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    fetchPreview(title, body, triggerType, campaignId);
  }, [fetchPreview, title, body, triggerType, campaignId]);

  // Re-render the preview when the campaign being edited changes. `title` and
  // `body` are deliberately NOT dependencies: they change on every keystroke,
  // and the preview is already refreshed by the handlers above and on blur.
  // Adding them here would fire a request per character.
  useEffect(() => {
    fetchPreview(title, body, triggerType, campaignId);
  }, [triggerType, campaignId, fetchPreview]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {titleLabel && <Label>{titleLabel}</Label>}
        {placeholders.length > 0 && (
          <button
            type="button"
            onClick={() => setShowHelp(!showHelp)}
            className="text-c-gray-400 hover:text-white text-xs underline"
            aria-expanded={showHelp}
          >
            {t("campaign.placeholders")}
          </button>
        )}
      </div>

      {showHelp && (
        <div className="bg-c-gray-500/60 border border-c-gray-400/20 rounded-lg p-3 text-xs text-c-gray-300">
          <p className="mb-2 font-medium text-white">
            {t("campaign.placeholdersAvailable")}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {placeholders.map(({ key, example, description }) => (
              <div key={key} className="flex items-baseline gap-2">
                <code className="text-accent whitespace-nowrap">{key}</code>
                <span className="text-c-gray-400" title={description}>
                  → {example}
                </span>
                <span className="text-c-gray-400 italic hidden lg:inline">
                  — {description}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Input
        disabled={disabled}
        value={title}
        onChange={(e) => handleTitleChange(e.target.value)}
        onBlur={handleBlur}
        placeholder="С днём рождения, {firstname}! 🎂"
      />

      <div className="flex gap-4 items-stretch">
        {/* Editor pane — built-in preview disabled */}
        <div className="flex-1 min-w-0">
          {bodyLabel && (
            <Label>
              <span className="text-c-gray-400 text-xs font-normal">
                {bodyLabel}
              </span>
            </Label>
          )}
          <div
            data-color-mode="dark"
            className="rounded-lg overflow-hidden md-editor-wrap"
          >
            <MDEditor
              value={body}
              onChange={handleBodyChange}
              onBlur={handleBlur}
              height={300}
              preview="none"
              toolbars={[
                "bold",
                "italic",
                "strike",
                "heading",
                "link",
                "olist",
                "ulist",
                "quote",
                "code",
                "codeBlock",
                "undo",
                "redo",
              ]}
            />
          </div>
        </div>

        {/* Preview pane — the rendered notification, title included */}
        <div className="hidden md:flex flex-col w-[375px] shrink-0">
          <div className="text-xs text-c-gray-400 mb-1 font-medium">
            {t("campaign.previewNotifications")}
          </div>
          <div
            data-color-mode="dark"
            className="flex-1 rounded-lg border border-c-gray-400/20 overflow-auto campaign-preview"
            style={{
              backgroundColor: "#1B283F",
            }}
          >
            <div className="px-4 pt-4 pb-1 text-sm font-semibold text-white border-b border-c-gray-400/10">
              {preview.title}
            </div>
            <div className="px-4 py-3">
              <MarkdownPreview
                source={preview.body}
                style={{
                  backgroundColor: "transparent",
                  color: "#f4f4f4",
                  fontSize: 14,
                  lineHeight: 1.6,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Hide the built-in editor preview pane */
        .md-editor-preview { display: none !important; }

        /* Hide line numbers gutter */
        .md-editor .cm-gutters { display: none !important; }
        .md-editor .cm-content { margin-left: 0 !important; }
        .md-editor .cm-lineNumbers { display: none !important; }

        /* Remove the CodeMirror left padding that creates space for line numbers */
        .md-editor .cm-scroller { padding-left: 0 !important; }

        /* Enable word wrap instead of horizontal scrolling */
        .md-editor .cm-content { white-space: pre-wrap !important; word-wrap: break-word !important; }
        .md-editor .cm-line { white-space: pre-wrap !important; }

        .cm-activeLine { background-color: transparent !important; }

        /* Mobile-like preview container */
        .campaign-preview .wmde-markdown {
          background-color: transparent;
          color: #f4f4f4;
          font-size: 14px;
          line-height: 1.6;
          padding: 0;
        }

        /* Dark mode markdown token colors */
        .campaign-preview .wmde-markdown,
        .campaign-preview[data-color-mode*='dark'] .wmde-markdown {
          --color-fg-default: #f4f4f4;
          --color-canvas-default: #1B283F;
          --color-canvas-subtle: #1B283F;
          --color-fg-muted: #727A88;
          --color-fg-subtle: #727A88;
          --color-border-muted: #2a3955;
        }
      `}</style>
    </div>
  );
}
